<script lang="ts" setup>
/**
 * Pill + dropdown for the AI-agent presence indicator. Slotted into
 * each page's title bar (DetailedView's button-group on most pages,
 * RunHeader's done-button slot on the interactive editor).
 *
 * The banner ("Following AI agent X") is a separate component
 * (AiPresenceBanner.vue) mounted globally in App.vue so it persists
 * across route changes / layout swaps. Both share state through
 * useAiPresence().
 */

import { computed, ref, onMounted, onBeforeUnmount } from "vue"
import { useStore } from "@/store/useStore"
import { useAiPresence, type AiSession } from "@/composables/useAiPresence"

const store = useStore()
const user = computed(() => store.getUserData())

const {
    active,
    sessions,
    sessionsCount,
    hasNewSession,
    ack,
    followingSessionId,
    followedSession,
    isFollowing,
    toggleFollow,
    stopFollowing,
} = useAiPresence()

const open = ref(false)
const root = ref<HTMLElement | null>(null)

function toggleOpen() {
    open.value = !open.value
    if (open.value) ack()
}

function closeOnOutside(e: MouseEvent) {
    if (!root.value) return
    if (e.target instanceof Node && root.value.contains(e.target)) return
    open.value = false
}

onMounted(() => document.addEventListener("click", closeOnOutside, true))
onBeforeUnmount(() => document.removeEventListener("click", closeOnOutside, true))

function onAgentRowClick(s: AiSession) {
    toggleFollow(s)
    open.value = false
}

const initials = computed(() => {
    const name = user.value?.fullName || user.value?.username || ""
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return "?"
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
})

function shortSession(id: string): string {
    return id.length > 8 ? id.slice(0, 8) : id
}

// The name shown in the pill. While following, the pill says just
// "Agent" — the session id is opaque to humans, so it's noise in the
// pill. The full short id is still shown in the dropdown (so the user
// can pick between concurrent agents) and in the banner.
const displayName = computed(() => {
    if (isFollowing.value) return "Agent"
    return user.value?.fullName || user.value?.username || ""
})

const displaySubtitle = computed(() => {
    if (isFollowing.value) {
        return followedSession.value?.activity?.label ?? "disconnected"
    }
    return null
})
</script>

<template lang="pug">
.ai-presence-widget(ref="root")
    button.profile-button(
        type="button",
        @click.stop="toggleOpen",
        :class="{ 'has-agent': active, 'has-new': hasNewSession, 'is-following': isFollowing }",
        :title="isFollowing ? 'Following AI agent — click to manage' : (active ? `${sessionsCount} AI agent${sessionsCount === 1 ? '' : 's'} active` : 'No AI agents connected')"
    )
        //- While following, the pill takes on the agent's identity.
        template(v-if="isFollowing")
            .avatar.agent
                i.fas.fa-robot
            .col.identity
                .name {{ displayName }}
                .subtitle(v-if="displaySubtitle") {{ displaySubtitle }}
        //- Otherwise it shows the logged-in user.
        template(v-else)
            .avatar {{ initials }}
            .name(v-if="displayName") {{ displayName }}
            .agent-indicator(v-if="active")
                i.fas.fa-robot
                span.count(v-if="sessionsCount > 1") {{ sessionsCount }}

    button.exit-follow(v-if="isFollowing", type="button", @click.stop="stopFollowing", title="Stop following — back to your view")
        i.fas.fa-times

    .dropdown-panel(v-if="open")
        .dropdown-section.user-section
            .row
                .avatar.lg {{ initials }}
                .col
                    .name {{ user?.fullName || user?.username || "Unknown user" }}
                    .email(v-if="user?.email") {{ user.email }}

        template(v-if="active")
            .dropdown-divider
            .dropdown-section.agents-section
                .section-title AI agents ({{ sessionsCount }})
                .row.agent-row(
                    v-for="s in sessions",
                    :key="s.mcpSessionId",
                    :class="{ 'is-active-follow': followingSessionId === s.mcpSessionId }",
                    @click="onAgentRowClick(s)"
                )
                    .avatar.lg.agent
                        i.fas.fa-robot
                    .col
                        .name
                            span Agent
                            span.session-id.ml-1 #[code {{ shortSession(s.mcpSessionId) }}]
                        .activity(v-if="s.activity?.label") {{ s.activity.label }}
                        .activity.muted(v-else) Idle
                        .follow-hint(v-if="followingSessionId === s.mcpSessionId") Following — click to stop
                        .follow-hint(v-else) Click to follow
        template(v-else)
            .dropdown-divider
            .dropdown-section.empty
                .muted No AI agents connected
</template>

<style lang="css" scoped>
.ai-presence-widget {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

/* When following, the pill shows the agent identity in two lines. */
.profile-button .col.identity {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.1;
    text-align: left;
}

.profile-button .col.identity .subtitle {
    font-size: 11px;
    color: rgb(242, 116, 5);
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.exit-follow {
    border: 1px solid rgb(242, 116, 5);
    background: rgb(242, 116, 5);
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    transition: background 0.15s ease;
}

.exit-follow:hover {
    background: rgb(200, 95, 0);
}

.profile-button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--light-grey, #e0e0e0);
    border-radius: 999px;
    padding: 4px 12px 4px 4px;
    cursor: pointer;
    color: var(--black, #222);
    font-size: 14px;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.profile-button:hover {
    background: var(--very-light-grey, #f4f4f4);
}

.profile-button.has-agent {
    border-color: rgb(17, 140, 24);
}

.profile-button.is-following {
    border-color: rgb(242, 116, 5);
    background: rgba(242, 116, 5, 0.08);
}

.profile-button .name {
    font-weight: 500;
}

.avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgb(17, 140, 24);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.avatar.lg {
    width: 36px;
    height: 36px;
    font-size: 14px;
}

.avatar.agent {
    background: rgb(242, 116, 5);
}

.agent-indicator {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgb(17, 140, 24);
    font-size: 13px;
    margin-left: 4px;
    position: relative;
}

.agent-indicator .count {
    font-weight: 600;
    font-size: 12px;
}

.profile-button.has-new .agent-indicator::after {
    content: "";
    position: absolute;
    top: -2px;
    right: -4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgb(242, 116, 5);
    box-shadow: 0 0 0 0 rgba(242, 116, 5, 0.7);
    animation: aiPulse 1.4s infinite;
}

@keyframes aiPulse {
    0% { box-shadow: 0 0 0 0 rgba(242, 116, 5, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(242, 116, 5, 0); }
    100% { box-shadow: 0 0 0 0 rgba(242, 116, 5, 0); }
}

.dropdown-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 280px;
    background: white;
    border: 1px solid var(--light-grey, #e0e0e0);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 1000;
    overflow: hidden;
}

.dropdown-section {
    padding: 12px 14px;
}

.dropdown-divider {
    height: 1px;
    background: var(--light-grey, #e0e0e0);
}

.section-title {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--mid-grey, #888);
    margin-bottom: 6px;
}

.row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
}

.col {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.name {
    font-weight: 500;
    font-size: 14px;
    line-height: 1.2;
}

.email,
.activity,
.follow-hint {
    font-size: 12px;
    color: var(--mid-grey, #666);
    line-height: 1.3;
}

.follow-hint {
    color: rgb(17, 140, 24);
    margin-top: 2px;
}

.is-active-follow .follow-hint {
    color: rgb(242, 116, 5);
    font-weight: 600;
}

.session-id {
    color: var(--mid-grey, #888);
    font-weight: normal;
}

.session-id code {
    background: var(--very-light-grey, #f4f4f4);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
}

.muted {
    color: var(--mid-grey, #888);
    font-style: italic;
}

.agent-row {
    cursor: pointer;
    border-radius: 6px;
    padding: 6px;
    margin-left: -6px;
    margin-right: -6px;
    align-items: flex-start;
    min-height: 56px;
}

.agent-row:hover {
    background: var(--very-light-grey, #f4f4f4);
}

.agent-row.is-active-follow {
    background: rgba(242, 116, 5, 0.08);
}

.agent-row .col {
    min-height: 44px;
}

.empty {
    text-align: center;
    font-size: 13px;
    padding: 16px;
}
</style>
